import React, { useState, useEffect } from 'react';
import { X, Search, RefreshCw, AlertTriangle, ArrowLeftRight, Check } from 'lucide-react';
import { searchFoods, reportIngredient } from '../services/clientApi';
import API_BASE_URL from '../config/api';

const REPORT_TYPES = [
  { value: 'misinformation', labelEn: 'Misinformation', labelHe: 'מידע שגוי' },
  { value: 'incorrect_values', labelEn: 'Incorrect nutritional values', labelHe: 'ערכים תזונתיים לא נכונים' },
  { value: 'wrong_name', labelEn: 'Wrong name or description', labelHe: 'שם או תיאור שגוי' },
  { value: 'wrong_portion', labelEn: 'Wrong portion or measure', labelHe: 'מידה או כמות שגויה' },
  { value: 'other', labelEn: 'Other', labelHe: 'אחר' },
];

const convertMeasurementWithAI = async (ingredient, fromMeasurement, toType, targetLang = 'en', client = { region: 'israel' }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/convert-measurement`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ingredient: ingredient.item || ingredient.name,
        brand: ingredient['brand of pruduct'] || ingredient.brand || '',
        fromMeasurement,
        toType, 
        targetLang,
        region: (client && client.region) ? client.region : 'israel'
      })
    });

    if (!response.ok) throw new Error(`Failed to convert measurement: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error in measurement conversion:', error);
    throw error;
  }
};

// recentFoods: ingredients this client has had before ({ item, grams, calories, protein, carbs, fat, brand, UPC }),
// offered as one-click picks while the search box is empty.
const AddIngredientModal = ({ visible, onClose, onAddIngredient, mealName, clientRegion, userCode, language = 'en', recentFoods = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [convertingMeasure, setConvertingMeasure] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [quantity, setQuantity] = useState(100);
  const [householdMeasure, setHouseholdMeasure] = useState('');
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportType, setReportType] = useState('incorrect_values');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [notice, setNotice] = useState('');
  const say = (he, en) => setNotice(language === 'hebrew' ? he : en);

  // --- PREMIUM DESIGN TOKENS ---
  const isDarkMode = document.documentElement.classList.contains('dark');
  const themeClasses = {
    inputField: 'w-full rounded-md border border-line dark:border-line-dark bg-surface dark:bg-canvas-dark px-4 py-2.5 text-sm text-ink dark:text-white outline-none placeholder:text-ink-muted dark:placeholder:text-ink-muted-dark transition-colors duration-120 focus:border-forest-ondark focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark',
    select: 'w-full rounded-md border border-line dark:border-line-dark bg-surface dark:bg-canvas-dark px-4 py-2.5 text-sm text-ink dark:text-white outline-none appearance-none bg-no-repeat pr-10 [&_option]:bg-surface [&_option]:text-ink dark:[&_option]:bg-surface-dark dark:[&_option]:text-white transition-colors duration-120 focus:border-forest-ondark focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark',
    btnPrimary: 'min-h-[44px] px-5 py-2.5 rounded-md font-semibold bg-forest-ondark text-white hover:bg-forest-ondark/90 dark:bg-forest dark:text-canvas-dark dark:hover:bg-forest-hover active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none transition-colors duration-120',
    btnSecondary: 'min-h-[44px] px-5 py-2.5 rounded-md font-semibold border border-line dark:border-line-dark bg-surface dark:bg-surface-dark text-ink dark:text-white hover:bg-moss dark:hover:bg-moss-dark active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 transition-colors duration-120',
    btnInfo: `px-4 py-2 rounded-lg font-medium bg-progress text-white hover:bg-progress  active:scale-[0.98] flex items-center justify-center gap-1.5 disabled:opacity-50 transition-all duration-200 text-xs sm:text-sm whitespace-nowrap`,
  };

  const isRTL = language === 'hebrew';

  useEffect(() => {
    if (visible) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedFood(null);
      setQuantity(100);
      setHouseholdMeasure('');
      setConvertingMeasure(false);
      setShowReportForm(false);
      setReportType('incorrect_values');
      setReportDescription('');
      setReportSubmitting(false);
      setNotice('');
    }
  }, [visible]);

  const handleConvertToHousehold = async () => {
    if (!selectedFood || !quantity || quantity <= 0) {
      say('אנא הזן כמות בגרם', 'Please enter a quantity in grams');
      return;
    }

    try {
      setConvertingMeasure(true);
      const result = await convertMeasurementWithAI(
        selectedFood,
        `${quantity}g`,
        'household',
        language === 'hebrew' ? 'he' : 'en',
        { region: clientRegion || 'israel' }
      );

      const measure = result.converted_measurement || result.household_measure || result.measure || result.text || '';
      if (measure && measure.trim()) {
        setHouseholdMeasure(measure.trim());
      } else {
        say('לא ניתן להמיר את המידה', 'Could not convert measurement');
      }
    } catch (error) {
      console.error('Error converting measurement:', error);
      say('שגיאה בהמרת המידה. נסה שוב.', 'Error converting measurement. Please try again.');
    } finally {
      setConvertingMeasure(false);
    }
  };

  const handleConvertToGrams = async () => {
    if (!selectedFood || !householdMeasure || !householdMeasure.trim()) {
      say('אנא הזן מידה ביתית', 'Please enter a household measure');
      return;
    }

    try {
      setConvertingMeasure(true);
      const result = await convertMeasurementWithAI(
        selectedFood,
        householdMeasure.trim(),
        'grams',
        language === 'hebrew' ? 'he' : 'en',
        { region: clientRegion || 'israel' }
      );

      const gramsText = result.converted_measurement || result.grams || result.measure || result.text || '';
      if (gramsText) {
        const gramsMatch = gramsText.toString().match(/(\d+(?:\.\d+)?)/);
        if (gramsMatch) {
          setQuantity(parseFloat(gramsMatch[1]));
        } else {
          say('לא ניתן להמיר את המידה', 'Could not convert measurement');
        }
      }
    } catch (error) {
      console.error('Error converting measurement:', error);
      say('שגיאה בהמרת המידה. נסה שוב.', 'Error converting measurement. Please try again.');
    } finally {
      setConvertingMeasure(false);
    }
  };

  useEffect(() => {
    const searchFood = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await searchFoods(searchQuery, 20);
        if (error) {
          console.error('Error searching foods:', error);
          setSearchResults([]);
        } else {
          setSearchResults(data || []);
        }
      } catch (err) {
        console.error('Unexpected error searching foods:', err);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchFood();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSelectFood = (food, grams = 100) => {
    setSelectedFood(food);
    setQuantity(grams);
    setHouseholdMeasure('');
  };

  // A recent ingredient → a food per 100 g (what the rest of this modal works with), at the grams it was used.
  const pickRecent = (r) => {
    const grams = Number(r.grams) || 100;
    const per100 = (v) => Math.round(((Number(v) || 0) / grams) * 1000) / 10;
    handleSelectFood({
      id: `recent-${r.item}`,
      name: r.item,
      brand: r.brand || '',
      upc: r.UPC || null,
      calories: Math.round(((Number(r.calories) || 0) / grams) * 100),
      protein: per100(r.protein),
      carbs: per100(r.carbs),
      fat: per100(r.fat),
    }, grams);
  };

  const handleAdd = () => {
    if (!selectedFood) return;

    const scale = quantity / 100;
    const ingredient = {
      UPC: selectedFood.upc || null,
      item: selectedFood.name || selectedFood.item,
      'brand of pruduct': selectedFood.brand || '',
      household_measure: householdMeasure || selectedFood.household_measure || '',
      'portionSI(gram)': quantity,
      calories: Math.round((selectedFood.calories || 0) * scale),
      protein: Math.round(((selectedFood.protein || 0) * scale) * 10) / 10,
      carbs: Math.round(((selectedFood.carbs || 0) * scale) * 10) / 10,
      fat: Math.round(((selectedFood.fat || 0) * scale) * 10) / 10,
    };

    onAddIngredient(ingredient);
    onClose();
  };

  const handleSubmitReport = async () => {
    if (!selectedFood) return;

    setReportSubmitting(true);
    try {
      const foodSnapshot = {
        name: selectedFood.name || selectedFood.item,
        brand: selectedFood.brand || '',
        calories: selectedFood.calories ?? 0,
        protein: selectedFood.protein ?? 0,
        carbs: selectedFood.carbs ?? 0,
        fat: selectedFood.fat ?? 0,
      };

      const { data, error } = await reportIngredient({
        foodId: selectedFood.id,
        foodSnapshot,
        reportType,
        description: reportDescription.trim() || undefined,
        userCode: userCode || undefined,
      });

      if (error) {
        say('שליחת הדיווח נכשלה. נסה שוב.', 'Failed to submit report. Please try again.');
        return;
      }

      setShowReportForm(false);
      setReportDescription('');
      setReportType('incorrect_values');
      say('תודה! הדיווח נשלח בהצלחה.', 'Thank you! Your report has been submitted.');
    } catch (e) {
      console.error('Report submit error:', e);
      say('שגיאה בשליחת הדיווח.', 'Error submitting report.');
    } finally {
      setReportSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-canvas-dark/60 sm:items-center sm:p-4">
      <div className={`bg-surface dark:bg-surface-dark rounded-t-xl sm:rounded-lg w-full max-w-2xl max-h-[92dvh] sm:max-h-[90vh] overflow-hidden flex flex-col border ${isDarkMode ? 'border-line-dark/60' : 'border-line/80'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-line dark:border-line-dark/80">
          <div>
            {mealName ? (
              <>
                <h2 className="text-ink dark:text-white text-2xl sm:text-3xl font-semibold tracking-tight mb-1">{mealName}</h2>
                <p className="text-ink-muted dark:text-ink-muted text-sm font-medium">
                  {language === 'hebrew' ? 'הוסף מרכיב' : 'Add Ingredient'}
                </p>
              </>
            ) : (
              <h2 className="text-ink dark:text-white text-2xl sm:text-3xl font-semibold tracking-tight">
                {language === 'hebrew' ? 'הוסף מרכיב' : 'Add Ingredient'}
              </h2>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-ink-muted hover:text-ink-muted dark:hover:text-ink-dark rounded-lg hover:bg-canvas dark:hover:bg-moss-dark/50 transition-colors">
            <X size={20} />
          </button>
        </div>

        {notice ? (
          <p className="px-5 sm:px-6 py-2 text-sm font-medium ind-fat bg-moss dark:bg-moss-dark" role="status">
            {notice}
          </p>
        ) : null}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
          {/* Search Setup */}
          {!selectedFood && (
            <div className="space-y-1.5 group relative">
              <label className="text-ink-muted dark:text-ink-muted-dark block text-xs font-bold uppercase tracking-wider">
                {language === 'hebrew' ? 'חפש מרכיב' : 'Search Ingredient'}
              </label>
              <div className="relative">
                <Search size={18} className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-ink-muted group-focus-within:text-forest transition-colors`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={language === 'hebrew' ? 'הקלד שם מרכיב...' : 'Type ingredient name...'}
                  className={`${themeClasses.inputField} ${isRTL ? 'pr-11 pl-4' : 'pl-11 pr-4'}`}
                />
              </div>
            </div>
          )}

          {/* Search Results Visual Layout Matrix */}
          {!selectedFood && (
            <>
              {loading && (
                <div className="text-center py-10 space-y-3">
                  <RefreshCw className="animate-spin h-8 w-8 text-forest mx-auto" />
                  <p className="text-ink-muted dark:text-ink-muted text-sm font-semibold animate-pulse">
                    {language === 'hebrew' ? 'מחפש...' : 'Searching...'}
                  </p>
                </div>
              )}

              {!loading && searchQuery.trim().length < 2 && recentFoods.length > 0 && (
                <div>
                  <p className="mb-2 text-[11px] font-medium text-ink-muted dark:text-ink-muted-dark">
                    {language === 'hebrew' ? 'אחרונים אצל הלקוח' : 'Recent for this client'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {recentFoods.map((r) => (
                      <button
                        key={r.item}
                        type="button"
                        onClick={() => pickRecent(r)}
                        className="min-h-11 rounded-md border border-line bg-canvas px-3 text-start text-[13px] text-ink transition-colors hover:border-forest-ondark hover:bg-moss focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest dark:border-line-dark dark:bg-surface-dark/40 dark:text-ink-dark dark:hover:bg-moss-dark"
                      >
                        <span className="font-medium">{r.item}</span>{' '}
                        <bdi dir="ltr" className="text-ink-muted dark:text-ink-muted-dark tabular-nums">{Math.round(Number(r.grams) || 0)} g</bdi>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!loading && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                <div className="text-center py-10 bg-canvas dark:bg-canvas-dark/30 border border-dashed border-line dark:border-line-dark rounded-lg">
                  <p className="text-ink-muted dark:text-ink-muted font-medium text-sm">
                    {language === 'hebrew' ? 'לא נמצאו תוצאות' : 'No results found'}
                  </p>
                </div>
              )}

              {!loading && searchResults.length > 0 && (
                <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1 custom-scrollbar">
                  {searchResults.map((food) => (
                    <button
                      key={food.id}
                      onClick={() => handleSelectFood(food)}
                      className={`w-full text-start p-4 rounded-lg border-2 transition-all  ${
                        selectedFood?.id === food.id
                          ? 'border-forest bg-forest-faint/40 dark:bg-forest-faint-dark/80'
                          : 'bg-canvas dark:bg-surface-dark/40 border-line/60 dark:border-line-dark hover:border-forest-ondark'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-ink dark:text-white font-bold text-base truncate">
                            {food.name || food.item}
                          </p>
                          {food.brand && (
                            <p className="text-ink-muted dark:text-ink-muted text-xs font-semibold mt-0.5">{food.brand}</p>
                          )}
                          <div className="flex gap-4 mt-2.5 text-xs font-bold">
                            <span className="text-ink-muted dark:text-ink-muted bg-surface dark:bg-surface-dark rounded px-2 py-0.5  border border-canvas dark:border-line-dark">
                              {food.calories || 0} {language === 'hebrew' ? 'קלוריות' : 'cal'}
                            </span>
                            <span className="text-ink-muted dark:text-ink-muted bg-surface dark:bg-surface-dark rounded px-2 py-0.5  border border-canvas dark:border-line-dark">
                              {food.protein || 0}g {language === 'hebrew' ? 'חלבון' : 'protein'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Selected Food Details Presentation Matrix */}
          {selectedFood && (
            <div className={`rounded-lg p-5 border ${isDarkMode ? 'bg-surface-dark/50 border-line-dark' : 'bg-canvas border-line/80'}  space-y-4`}>
              <h3 className="text-ink dark:text-ink-dark font-bold text-base border-b border-line dark:border-line-dark pb-2">
                {language === 'hebrew' ? 'פרטי המרכיב' : 'Ingredient Details'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-ink-muted dark:text-ink-muted-dark block text-xs font-bold uppercase tracking-wider mb-2">
                    {language === 'hebrew' ? 'כמות (גרם)' : 'Quantity (grams)'}
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
                      min="1"
                      className={themeClasses.inputField}
                    />
                    <button
                      onClick={handleConvertToHousehold}
                      disabled={convertingMeasure || !quantity || quantity <= 0}
                      className={themeClasses.btnInfo}
                    >
                      {convertingMeasure ? (
                        <RefreshCw size={16} className="animate-spin" />
                      ) : (
                        <>
                          <ArrowLeftRight size={14} />
                          <span>{language === 'hebrew' ? 'ביתית' : 'Household'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-ink-muted dark:text-ink-muted-dark block text-xs font-bold uppercase tracking-wider mb-2">
                    {language === 'hebrew' ? 'מידה ביתית' : 'Household Measure'}
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={householdMeasure}
                      onChange={(e) => setHouseholdMeasure(e.target.value)}
                      placeholder={language === 'hebrew' ? 'לדוגמה: 1 כוס, 2 כפות' : 'e.g., 1 cup, 2 tbsp'}
                      className={themeClasses.inputField}
                    />
                    <button
                      onClick={handleConvertToGrams}
                      disabled={convertingMeasure || !householdMeasure || !householdMeasure.trim()}
                      className={themeClasses.btnInfo}
                    >
                      {convertingMeasure ? (
                        <RefreshCw size={16} className="animate-spin" />
                      ) : (
                        <>
                          <ArrowLeftRight size={14} />
                          <span>{language === 'hebrew' ? 'גרם' : 'Grams'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Calculated Nutrition Block Card Display */}
                <div className={`rounded-lg p-4 border bg-surface dark:bg-canvas-dark/30 ${isDarkMode ? 'border-line-dark' : 'border-line'}`}>
                  <p className="text-ink-muted dark:text-ink-muted text-xs font-bold uppercase tracking-wider mb-3">
                    {language === 'hebrew' ? 'ערכים תזונתיים (לכמות שנבחרה)' : 'Nutritional Values (for selected quantity)'}
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm font-semibold">
                    <div className="flex justify-between border-b border-canvas dark:border-surface-dark pb-1">
                      <span className="text-ink-muted dark:text-ink-muted">{language === 'hebrew' ? 'קלוריות: ' : 'Calories: '}</span>
                      <span className="text-ink dark:text-white font-bold">{Math.round((selectedFood.calories || 0) * (quantity / 100))}</span>
                    </div>
                    <div className="flex justify-between border-b border-canvas dark:border-surface-dark pb-1">
                      <span className="text-ink-muted dark:text-ink-muted">{language === 'hebrew' ? 'חלבון: ' : 'Protein: '}</span>
                      <span className="text-forest-ondark dark:text-forest font-bold">{Math.round(((selectedFood.protein || 0) * (quantity / 100)) * 10) / 10}g</span>
                    </div>
                    <div className="flex justify-between sm:border-b-0 pb-1">
                      <span className="text-ink-muted dark:text-ink-muted">{language === 'hebrew' ? 'פחמימות: ' : 'Carbs: '}</span>
                      <span className="text-progress dark:text-progress font-bold">{Math.round(((selectedFood.carbs || 0) * (quantity / 100)) * 10) / 10}g</span>
                    </div>
                    <div className="flex justify-between pb-1">
                      <span className="text-ink-muted dark:text-ink-muted">{language === 'hebrew' ? 'שומן: ' : 'Fat: '}</span>
                      <span className="text-amber-600 dark:text-amber-400 font-bold">{Math.round(((selectedFood.fat || 0) * (quantity / 100)) * 10) / 10}g</span>
                    </div>
                  </div>
                </div>

                {/* Report button & error form elements block */}
                <div className="pt-3 border-t border-line dark:border-line-dark/80">
                  {!showReportForm ? (
                    <button
                      type="button"
                      onClick={() => setShowReportForm(true)}
                      className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-muted dark:text-ink-muted hover:text-amber-500 dark:hover:text-amber-400 transition-colors"
                    >
                      <AlertTriangle size={15} />
                      {language === 'hebrew' ? 'דווח על שגיאה' : 'Report an error'}
                    </button>
                  ) : (
                    <div className="bg-surface dark:bg-canvas-dark/40 border border-line dark:border-line-dark rounded-lg p-4 space-y-4 ">
                      <p className="text-ink dark:text-ink-dark text-sm font-bold flex items-center gap-1.5">
                        <AlertTriangle size={16} className="text-amber-500" />
                        {language === 'hebrew' ? 'מה לא נכון?' : "What's wrong?"}
                      </p>
                      
                      <div className="relative">
                        <select
                          value={reportType}
                          onChange={(e) => setReportType(e.target.value)}
                          className={themeClasses.select + ' !py-2 '}
                        >
                          {REPORT_TYPES.map((r) => (
                            <option key={r.value} value={r.value}>
                              {language === 'hebrew' ? r.labelHe : r.labelEn}
                            </option>
                          ))}
                        </select>
                        <ChevronRight size={14} className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-ink-muted rotate-90 pointer-events-none`} />
                      </div>

                      <textarea
                        value={reportDescription}
                        onChange={(e) => setReportDescription(e.target.value)}
                        placeholder={language === 'hebrew' ? 'פרטים (אופציונלי)' : 'Details (optional)'}
                        rows={2}
                        className={themeClasses.inputField + ' !py-2 resize-none '}
                      />
                      <div className="flex gap-2 justify-end pt-1">
                        <button
                          type="button"
                          onClick={() => { setShowReportForm(false); setReportDescription(''); setReportType('incorrect_values'); }}
                          disabled={reportSubmitting}
                          className={themeClasses.btnSecondary + ' !py-1.5 !px-3 !text-xs'}
                        >
                          {language === 'hebrew' ? 'ביטול' : 'Cancel'}
                        </button>
                        <button
                          type="button"
                          onClick={handleSubmitReport}
                          disabled={reportSubmitting}
                          className="px-4 py-1.5 rounded-lg text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 active:scale-[0.98] transition-all flex items-center gap-1 "
                        >
                          {reportSubmitting ? <RefreshCw size={12} className="animate-spin" /> : <Check size={12} strokeWidth={3} />}
                          {language === 'hebrew' ? 'שלח דיווח' : 'Submit report'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Container Segment Row */}
        <div className="flex items-center justify-end gap-3 p-5 sm:p-6 border-t border-line dark:border-line-dark/80 bg-canvas/50 dark:bg-canvas-dark/30">
          <button onClick={onClose} className={themeClasses.btnSecondary}>
            {language === 'hebrew' ? 'ביטול' : 'Cancel'}
          </button>
          <button
            onClick={handleAdd}
            disabled={!selectedFood}
            className={themeClasses.btnPrimary}
          >
            {language === 'hebrew' ? 'הוסף' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Chevron display lookup compatibility definition logic helper element block
function ChevronRight({ size, className }) {
  return (
    <svg width={size} height={size} className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

export default AddIngredientModal;